import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faArrowsRotate,
    faCircleCheck,
    faCircleExclamation,
    faCircleXmark,
    faGamepad,
    faPen,
    faTrash,
    faPlug,
} from "@fortawesome/free-solid-svg-icons";
import Button from "../../components/Button/Button";
import Popup from "../../components/Popup/Popup";
import {
    createProject,
    deleteProject,
    disconnectAccount,
    fetchApiHealth,
    fetchPlatforms,
    fetchProjects,
    fetchRobloxGames,
    getConnectUrl,
    linkRobloxGame,
    renameProject,
    syncAccount,
    unlinkRobloxGame,
} from "../../redux/marketingSlice";
import { ACCOUNT_STATUS, CONNECTABLE_PLATFORMS, PLATFORMS, formatDateTime } from "./marketingUtils";

const STATUS_ICONS = { good: faCircleCheck, warning: faCircleExclamation, critical: faCircleXmark };

const CALLBACK_ERRORS = {
    invalid_state: "La autorización expiró o no es válida. Inténtalo de nuevo.",
    missing_code: "La red social no devolvió el código de autorización.",
    connect_failed: "No se pudo completar la conexión con la red social.",
    no_accounts: "La cuenta autorizada no tiene canales o perfiles que conectar.",
};

export function StatusBadge({ tone, label }) {
    return (
        <span className={`mk-status mk-status--${tone}`}>
            <FontAwesomeIcon icon={STATUS_ICONS[tone]} />
            {label}
        </span>
    );
}

// Lee los parámetros con los que la API redirige después del OAuth.
function readCallback(search) {
    const params = new URLSearchParams(search);
    const status = params.get("status");
    if (!status) return null;
    const platform = PLATFORMS[params.get("platform")]?.label || params.get("platform") || "La red social";
    const connected = Number(params.get("connected") || 0);
    const conflicts = params.get("conflicts");
    const error = params.get("error");

    if (status === "error") {
        return { tone: "critical", text: `${platform}: ${CALLBACK_ERRORS[error] || error || "Error al conectar"}` };
    }
    const parts = [`${platform}: ${connected} cuenta(s) conectada(s).`];
    if (conflicts) parts.push(`Ya pertenecen a otro proyecto: ${conflicts}.`);
    return { tone: status === "partial" ? "warning" : "good", text: parts.join(" ") };
}

function ConnectionHealth() {
    const { apiHealth, platforms } = useSelector((state) => state.marketing);
    const apiOk = apiHealth?.status === "ok" && apiHealth?.database === "up";

    return (
        <section className="mk-panel mk-health">
            <h2 className="mk-panel-title">Estado de conexiones</h2>
            <div className="mk-health-row">
                <div className="mk-health-item">
                    <span className="mk-health-label">API</span>
                    {apiHealth ? (
                        <StatusBadge tone={apiOk ? "good" : "critical"} label={apiOk ? "En línea" : "Sin conexión"} />
                    ) : (
                        <span className="mk-muted">Verificando…</span>
                    )}
                </div>
                {CONNECTABLE_PLATFORMS.map((key) => {
                    const configured = platforms.find((item) => item.platform === key)?.configured;
                    return (
                        <div className="mk-health-item" key={key}>
                            <span className="mk-health-label">
                                <FontAwesomeIcon icon={PLATFORMS[key].icon} /> {PLATFORMS[key].label}
                            </span>
                            {platforms.length === 0 ? (
                                <span className="mk-muted">—</span>
                            ) : (
                                <StatusBadge
                                    tone={configured ? "good" : "warning"}
                                    label={configured ? "Configurada" : "Faltan credenciales en el servidor"}
                                />
                            )}
                        </div>
                    );
                })}
            </div>
        </section>
    );
}

function AccountRow({ account, onFeedback, onReload, onConnect }) {
    const dispatch = useDispatch();
    const [busy, setBusy] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const status = ACCOUNT_STATUS[account.status] || ACCOUNT_STATUS.disconnected;
    const platform = PLATFORMS[account.platform];

    const handleSync = async () => {
        setBusy(true);
        try {
            const result = await dispatch(syncAccount({ id: account._id })).unwrap();
            onFeedback({
                tone: "good",
                text: `@${account.username}: ${result?.linked ?? 0} publicaciones vinculadas, ${result?.unassigned ?? 0} nuevas sin asignar.`,
            });
            onReload();
        } catch (error) {
            onFeedback({ tone: "critical", text: `@${account.username}: ${error}` });
        } finally {
            setBusy(false);
        }
    };

    const handleDisconnect = async () => {
        setBusy(true);
        try {
            await dispatch(disconnectAccount({ id: account._id })).unwrap();
            onFeedback({ tone: "good", text: `@${account.username} desconectada.` });
            setConfirmOpen(false);
            onReload();
        } catch (error) {
            onFeedback({ tone: "critical", text: error });
        } finally {
            setBusy(false);
        }
    };

    return (
        <tr>
            <td>
                <span className="mk-account">
                    {account.avatarUrl ? (
                        <img className="mk-avatar" src={account.avatarUrl} alt="" />
                    ) : (
                        <FontAwesomeIcon icon={platform?.icon} className="mk-avatar mk-avatar--icon" />
                    )}
                    <span>
                        <strong>{account.displayName || account.username}</strong>
                        <span className="mk-muted">
                            <FontAwesomeIcon icon={platform?.icon} /> @{account.username}
                        </span>
                    </span>
                </span>
            </td>
            <td><StatusBadge tone={status.tone} label={status.label} /></td>
            <td>{formatDateTime(account.lastSyncedAt)}</td>
            <td className="mk-actions">
                {account.status === "reauth_required" ? (
                    <Button variant="primary" size="small" onClick={() => onConnect(account.platform)}>
                        Reconectar
                    </Button>
                ) : (
                    <Button variant="outline" size="small" onClick={handleSync} disabled={busy} title="Buscar publicaciones nuevas ahora">
                        <FontAwesomeIcon icon={faArrowsRotate} spin={busy} /> Sincronizar
                    </Button>
                )}
                <button type="button" className="mk-icon-btn" onClick={() => setConfirmOpen(true)} aria-label="Desconectar cuenta" title="Desconectar">
                    <FontAwesomeIcon icon={faTrash} />
                </button>
                <Popup
                    isOpen={confirmOpen}
                    title="Desconectar cuenta"
                    onClose={() => setConfirmOpen(false)}
                    onConfirm={handleDisconnect}
                    confirmText="Desconectar"
                    confirmDisabled={busy}
                >
                    <p>
                        Se revocará el acceso a @{account.username}. Las publicaciones y métricas ya guardadas se conservan.
                    </p>
                </Popup>
            </td>
        </tr>
    );
}

function RobloxGames({ projectId, onFeedback }) {
    const dispatch = useDispatch();
    const games = useSelector((state) => state.marketing.robloxGames[projectId]);
    const [place, setPlace] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        dispatch(fetchRobloxGames({ projectId }));
    }, [dispatch, projectId]);

    const handleLink = async (event) => {
        event.preventDefault();
        if (!place.trim()) return;
        setBusy(true);
        try {
            const game = await dispatch(linkRobloxGame({ projectId, place: place.trim() })).unwrap();
            onFeedback({ tone: "good", text: `Juego vinculado: ${game?.name}.` });
            setPlace("");
            dispatch(fetchRobloxGames({ projectId }));
        } catch (error) {
            onFeedback({ tone: "critical", text: error });
        } finally {
            setBusy(false);
        }
    };

    const handleUnlink = async (game) => {
        try {
            await dispatch(unlinkRobloxGame({ id: game._id })).unwrap();
            onFeedback({ tone: "good", text: `Juego desvinculado: ${game.name}.` });
            dispatch(fetchRobloxGames({ projectId }));
        } catch (error) {
            onFeedback({ tone: "critical", text: error });
        }
    };

    return (
        <div className="mk-roblox">
            <h4 className="mk-subtitle"><FontAwesomeIcon icon={faGamepad} /> Juegos de Roblox</h4>
            {games?.length ? (
                <ul className="mk-chip-list">
                    {games.map((game) => (
                        <li key={game._id} className="mk-chip">
                            <span>{game.name}</span>
                            <span className="mk-muted">Place {game.placeId}</span>
                            <button type="button" className="mk-icon-btn" onClick={() => handleUnlink(game)} aria-label={`Desvincular ${game.name}`}>
                                <FontAwesomeIcon icon={faTrash} />
                            </button>
                        </li>
                    ))}
                </ul>
            ) : (
                <p className="mk-muted">Sin juegos vinculados. Vincula uno para ver usuarios y retención.</p>
            )}
            <form className="mk-inline-form" onSubmit={handleLink}>
                <input
                    type="text"
                    value={place}
                    onChange={(event) => setPlace(event.target.value)}
                    placeholder="Place ID o URL roblox.com/games/…"
                    maxLength={300}
                />
                <Button type="submit" variant="outline" size="small" disabled={busy || !place.trim()}>
                    Vincular
                </Button>
            </form>
        </div>
    );
}

function ProjectCard({ project, onFeedback, onReload }) {
    const dispatch = useDispatch();
    const platforms = useSelector((state) => state.marketing.platforms);
    const [editing, setEditing] = useState(false);
    const [name, setName] = useState(project.name);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [connecting, setConnecting] = useState(null);

    const isConfigured = (key) => platforms.find((item) => item.platform === key)?.configured;

    const handleConnect = async (platform) => {
        setConnecting(platform);
        try {
            const url = await dispatch(getConnectUrl({ platform, projectId: project._id })).unwrap();
            window.location.assign(url);
        } catch (error) {
            onFeedback({ tone: "critical", text: error });
            setConnecting(null);
        }
    };

    const handleRename = async (event) => {
        event.preventDefault();
        try {
            await dispatch(renameProject({ id: project._id, name: name.trim() })).unwrap();
            setEditing(false);
            onReload();
        } catch (error) {
            onFeedback({ tone: "critical", text: error });
        }
    };

    const handleDelete = async () => {
        try {
            await dispatch(deleteProject({ id: project._id })).unwrap();
            onFeedback({ tone: "good", text: `Proyecto "${project.name}" eliminado.` });
        } catch (error) {
            onFeedback({ tone: "critical", text: error });
        }
        setConfirmDelete(false);
    };

    return (
        <section className="mk-panel mk-project">
            <header className="mk-project-header">
                {editing ? (
                    <form className="mk-inline-form" onSubmit={handleRename}>
                        <input value={name} onChange={(event) => setName(event.target.value)} maxLength={100} autoFocus />
                        <Button type="submit" variant="primary" size="small" disabled={!name.trim()}>Guardar</Button>
                        <Button variant="outline" size="small" onClick={() => { setEditing(false); setName(project.name); }}>Cancelar</Button>
                    </form>
                ) : (
                    <h3 className="mk-project-name">{project.name}</h3>
                )}
                {!editing && (
                    <div className="mk-actions">
                        <button type="button" className="mk-icon-btn" onClick={() => setEditing(true)} aria-label="Renombrar proyecto" title="Renombrar">
                            <FontAwesomeIcon icon={faPen} />
                        </button>
                        <button type="button" className="mk-icon-btn" onClick={() => setConfirmDelete(true)} aria-label="Eliminar proyecto" title="Eliminar">
                            <FontAwesomeIcon icon={faTrash} />
                        </button>
                    </div>
                )}
            </header>

            <h4 className="mk-subtitle"><FontAwesomeIcon icon={faPlug} /> Redes sociales</h4>
            {project.accounts?.length ? (
                <div className="mk-table-wrap">
                    <table className="mk-table">
                        <thead>
                            <tr>
                                <th>Cuenta</th>
                                <th>Estado</th>
                                <th>Última sincronización</th>
                                <th aria-label="Acciones" />
                            </tr>
                        </thead>
                        <tbody>
                            {project.accounts.map((account) => (
                                <AccountRow
                                    key={account._id}
                                    account={account}
                                    onFeedback={onFeedback}
                                    onReload={onReload}
                                    onConnect={handleConnect}
                                />
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <p className="mk-muted">Este proyecto aún no tiene cuentas conectadas.</p>
            )}

            <div className="mk-connect-row">
                {CONNECTABLE_PLATFORMS.map((key) => (
                    <Button
                        key={key}
                        variant="outline"
                        size="small"
                        onClick={() => handleConnect(key)}
                        disabled={!isConfigured(key) || connecting !== null}
                        title={isConfigured(key) ? `Conectar una cuenta de ${PLATFORMS[key].label}` : "La plataforma no está configurada en el servidor"}
                    >
                        <FontAwesomeIcon icon={PLATFORMS[key].icon} /> {connecting === key ? "Redirigiendo…" : `Conectar ${PLATFORMS[key].label}`}
                    </Button>
                ))}
            </div>

            <RobloxGames projectId={project._id} onFeedback={onFeedback} />

            <Popup
                isOpen={confirmDelete}
                title="Eliminar proyecto"
                onClose={() => setConfirmDelete(false)}
                onConfirm={handleDelete}
                confirmText="Eliminar"
            >
                <p>
                    ¿Eliminar "{project.name}"? Solo se puede eliminar si no tiene publicaciones ni cuentas conectadas.
                </p>
            </Popup>
        </section>
    );
}

export default function Accounts() {
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const { projects, projectsStatus, projectsError } = useSelector((state) => state.marketing);
    const [feedback, setFeedback] = useState(() => readCallback(location.search));
    const [newName, setNewName] = useState("");
    const [creating, setCreating] = useState(false);

    const reload = () => dispatch(fetchProjects());

    useEffect(() => {
        dispatch(fetchProjects());
        dispatch(fetchPlatforms());
        dispatch(fetchApiHealth());
    }, [dispatch]);

    // Limpia los parámetros del OAuth de la URL una vez leídos.
    useEffect(() => {
        if (location.search) navigate(location.pathname, { replace: true });
    }, [location.search, location.pathname, navigate]);

    const handleCreate = async (event) => {
        event.preventDefault();
        setCreating(true);
        try {
            await dispatch(createProject({ name: newName.trim() })).unwrap();
            setFeedback({ tone: "good", text: `Proyecto "${newName.trim()}" creado.` });
            setNewName("");
            reload();
        } catch (error) {
            setFeedback({ tone: "critical", text: error });
        } finally {
            setCreating(false);
        }
    };

    return (
        <div className="mk-scroll">
            {feedback && (
                <div className={`mk-banner mk-banner--${feedback.tone}`} role="status">
                    <FontAwesomeIcon icon={STATUS_ICONS[feedback.tone]} />
                    <span>{feedback.text}</span>
                    <button type="button" className="mk-icon-btn" onClick={() => setFeedback(null)} aria-label="Cerrar aviso">
                        <FontAwesomeIcon icon={faCircleXmark} />
                    </button>
                </div>
            )}

            <ConnectionHealth />

            <section className="mk-panel">
                <h2 className="mk-panel-title">Nuevo proyecto</h2>
                <form className="mk-inline-form" onSubmit={handleCreate}>
                    <input
                        type="text"
                        value={newName}
                        onChange={(event) => setNewName(event.target.value)}
                        placeholder="Nombre del proyecto (ej. Tower of Myths)"
                        maxLength={100}
                    />
                    <Button type="submit" variant="primary" size="small" disabled={creating || !newName.trim()}>
                        Crear proyecto
                    </Button>
                </form>
            </section>

            {projectsStatus === "loading" && projects.length === 0 && (
                <div className="mk-center"><div className="mk-spinner" /><span>Cargando proyectos…</span></div>
            )}
            {projectsStatus === "failed" && <div className="mk-center mk-error">{projectsError}</div>}
            {projectsStatus === "succeeded" && projects.length === 0 && (
                <div className="mk-center">Aún no hay proyectos. Crea el primero arriba.</div>
            )}

            {projects.map((project) => (
                <ProjectCard key={project._id} project={project} onFeedback={setFeedback} onReload={reload} />
            ))}
        </div>
    );
}
